/* eslint-disable no-param-reassign */
import axios from 'axios';

const mainApiInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  withCredentials: true,
});

// --- Access token management (sessionStorage) ---
const ACCESS_TOKEN_KEY = 'accessToken';

export const setAccessToken = (token) => {
  if (token) {
    sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
  } else {
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  }
};

export const getAccessToken = () => sessionStorage.getItem(ACCESS_TOKEN_KEY);

// --- 401 refresh queue ---
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// --- Request interceptor ---
mainApiInstance.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// --- Response interceptor (401 → refresh → retry) ---
mainApiInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;

    // Skip interceptor for auth mutation routes to avoid infinite loops
    // (but NOT /v2/auth/me — that needs the refresh logic)
    const skipUrls = [
      '/v2/auth/login',
      '/v2/auth/logout',
      '/v2/auth/refresh-token',
    ];
    if (
      skipUrls.some((url) => originalRequest.url.includes(url)) ||
      originalRequest._retry
    ) {
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 401) {
      if (isRefreshing) {
        // Another refresh is already in-flight — queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return mainApiInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // Check if user was previously authenticated
      const hadToken = !!getAccessToken();

      return new Promise((resolve, reject) => {
        axios
          .post(
            `${process.env.REACT_APP_BASE_URL}/v2/auth/refresh-token`,
            {},
            { withCredentials: true },
          )
          .then((res) => {
            const newToken = res.data.result.accessToken;
            setAccessToken(newToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            processQueue(null, newToken);
            resolve(mainApiInstance(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            setAccessToken(null);
            // Only redirect if user was previously logged in (session expired)
            // Skip redirect on initial page load (no prior session) to avoid infinite loop
            if (hadToken) {
              window.location.href = '/';
            }
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    return Promise.reject(error);
  },
);

export default mainApiInstance;
